import { Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';
import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  useBindRetailEvotorPriceListStore,
  useBindRetailEvotorStoreLocation,
  useImportRetailEvotorStores,
  useRetailEvotorAccounts,
  useRetailEvotorPriceListsOverview,
  useRetailEvotorStores,
  useSetRetailEvotorStorePriceExport,
  useSetRetailEvotorStoreStockExport,
  useStoreLocations,
  useToggleRetailEvotorStore,
  useUnbindRetailEvotorPriceListStore,
  useUnbindRetailEvotorStoreLocation,
} from '../../hooks';
import { useDomainPermissions } from '../../lib';
import type {
  RetailEvotorNotExportingPriceReason,
  RetailEvotorNotExportingReason,
  RetailEvotorStore,
} from '../../model/settingsTypes';

import { ConfirmSyncDialog } from './ConfirmSyncDialog';
import { RetailEvotorLocationSelect } from './RetailEvotorLocationSelect';
import { RetailEvotorPriceListSelect } from './RetailEvotorPriceListSelect';
import { SettingsCard } from './SettingsCard';

/** A price-list rebind (moving from one list to another) or an unbind (picking "not bound") —
 * both need a confirm (design spec §8.7); only the FIRST-TIME bind (currently unbound) is
 * immediate. Always carries the current binding, since both actions need to name/target it. */
interface PriceListActionState {
  store: RetailEvotorStore;
  currentCode: string;
  currentName: string;
  /** `null` = unbind; a code = rebind to that list. */
  nextCode: string | null;
  nextName?: string;
}

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/** Maps the BFF's `notExportingReason` enum to its `settings.json` translation key. An unknown
 * future value (not yet in this map) falls back to rendering the raw string rather than crashing
 * — see `feedback_enum-label-fallback-tolerance` precedent elsewhere in this app. */
const NOT_EXPORTING_REASON_KEYS: Record<RetailEvotorNotExportingReason, string> = {
  NOT_BOUND: 'notBound',
  LOCATION_INACTIVE: 'locationInactive',
  EXPORT_DISABLED: 'exportDisabled',
  TENANT_PAUSED: 'tenantPaused',
  PLATFORM_DISABLED: 'platformDisabled',
  STORE_INACTIVE: 'storeInactive',
  ACCOUNT_INACTIVE: 'accountInactive',
};

/** The `NOT_EXPORTING_REASON_KEYS` twin for `notExportingPriceReason` (adr-integration-017 as
 * amended 2026-09-06) — maps the BFF's price-export reason enum to its `settings.json` key under
 * `notExportingPriceReason`. Same unknown-value tolerance as the stock map above. */
const NOT_EXPORTING_PRICE_REASON_KEYS: Record<RetailEvotorNotExportingPriceReason, string> = {
  PLATFORM_DISABLED: 'platformDisabled',
  TENANT_PAUSED: 'tenantPaused',
  NOT_BOUND: 'notBound',
  LOCATION_INACTIVE: 'locationInactive',
  EXPORT_DISABLED: 'exportDisabled',
  STORE_INACTIVE: 'storeInactive',
  ACCOUNT_INACTIVE: 'accountInactive',
};

export interface RetailEvotorStoresPanelProps {
  canManage: boolean;
  onGoToAccountsTab: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Stores tab — self-contained (own filter/page/size state + query), mirroring the
 * `MaxSettingsPage` secondary-query precedent rather than lifting state to the page (unlike the
 * Accounts tab, `SettingsScaffold`'s primary query is the accounts list, so Stores doesn't need
 * to feed it).
 */
export function RetailEvotorStoresPanel({
  canManage,
  onGoToAccountsTab,
  onSuccess,
  onError,
}: RetailEvotorStoresPanelProps) {
  const { t } = useTranslation('settings');
  // adr-pricing-025 d9 — the price-list select AND the price-export toggle are both gated by the
  // admin-tier `settings.pricing.manage`, independently of this panel's own `canManage`
  // (`settings.retail-evotor.manage`). Confirmed against the shipped BFF contract 2026-09-03: the
  // price-export endpoint's own doc comment states this explicitly ("deliberately NOT
  // settings.retail-evotor.manage like every other write on this tag") — a genuine deviation from
  // the design spec's §4/§8.7 text, which assumed the price-export toggle rode the vendor
  // `settings.retail-evotor.manage` tier alongside sync/stock-export.
  const { canManage: canManagePricing } = useDomainPermissions('pricing');
  const [filterInput, setFilterInput] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const query = useRetailEvotorStores({
    name: filterInput.trim() || undefined,
    page,
    size,
    sort: 'createdAt:desc',
  });
  // Unfiltered, minimal existence check — deciding which empty state to render must not depend on
  // whatever the (separate) Accounts tab's own search filter happens to be at the moment.
  const accountsExistenceQuery = useRetailEvotorAccounts({ page: 0, size: 1 });
  const toggleMutation = useToggleRetailEvotorStore();
  const importMutation = useImportRetailEvotorStores();

  // Locations for the bind picker (plan-integration-010 Phase 6, user-story-integration-024).
  // Fetched without `activeOnly` — like `StoreProfileSettingsPage`'s own `allLocations` picker —
  // so a store's current (possibly since-deactivated) binding still resolves to a real option
  // instead of vanishing from the list; `RetailEvotorLocationSelect` disables inactive ones as
  // NEW bind targets instead.
  const locationsQuery = useStoreLocations({ page: 0, size: 100 });
  const locations = locationsQuery.data?.items ?? [];
  const locationsUnavailable = locationsQuery.isError;
  const bindMutation = useBindRetailEvotorStoreLocation();
  const unbindMutation = useUnbindRetailEvotorStoreLocation();
  const stockExportMutation = useSetRetailEvotorStoreStockExport();
  const [stockExportConfirmStore, setStockExportConfirmStore] = useState<RetailEvotorStore | null>(
    null,
  );

  // "Price list" select + "Price export" toggle (design spec §8.7, adr-integration-017).
  const priceListsOverview = useRetailEvotorPriceListsOverview();
  const bindPriceListMutation = useBindRetailEvotorPriceListStore();
  const unbindPriceListMutation = useUnbindRetailEvotorPriceListStore();
  const priceExportMutation = useSetRetailEvotorStorePriceExport();
  const [priceListAction, setPriceListAction] = useState<PriceListActionState | null>(null);
  const [priceExportConfirmStore, setPriceExportConfirmStore] = useState<RetailEvotorStore | null>(
    null,
  );

  const data = query.data;
  // Memoized so the `[]` fallback doesn't create a new array identity on every render while
  // `data` itself is unchanged — `locationShareCounts` below depends on it.
  const stores = useMemo(() => data?.content ?? [], [data]);
  const hasFilter = filterInput.trim() !== '';
  const hasAnyAccounts = (accountsExistenceQuery.data?.page.totalElements ?? 0) > 0;
  const showLinearProgress = query.isFetching && !query.isLoading;

  // How many *listed* (current page) stores resolve to each location — the shared-stock hint
  // (AC2 of user-story-integration-024) is deliberately scoped to what's on screen, since there
  // is no endpoint to aggregate this across the whole, possibly multi-page, store list.
  const locationShareCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of stores) {
      if (!s.locationId) continue;
      counts.set(s.locationId, (counts.get(s.locationId) ?? 0) + 1);
    }
    return counts;
  }, [stores]);

  // Same page-clamp rationale as `RetailEvotorAccountsPanel` — keep `page` in range after the
  // last store on the last page is toggled off/removed upstream, instead of showing a misleading
  // empty state while stores still exist on an earlier page.
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(0, Math.ceil(data.page.totalElements / size) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [data, page, size]);

  const handleToggle = (store: RetailEvotorStore) => {
    toggleMutation.mutate(
      { evotorId: store.evotorId, active: !(store.active ?? false) },
      {
        onSuccess: (message) => onSuccess(message),
        onError: (error) => onError(error.message),
      },
    );
  };

  /** Binds — or re-binds (AC3: replaces the previous binding) — a store to a location. */
  const handleBind = (store: RetailEvotorStore, locationId: string) => {
    if (!locationId || locationId === (store.locationId ?? '')) return;
    const location = locations.find((candidate) => candidate.id === locationId);
    bindMutation.mutate(
      { evotorId: store.evotorId, locationId },
      {
        onSuccess: () =>
          onSuccess(
            t('retailSettings.stores.bindFeedback', {
              name: store.name || store.evotorId,
              location: location?.name ?? locationId,
            }),
          ),
        onError: (error) => onError(error.message),
      },
    );
  };

  const handleUnbind = (store: RetailEvotorStore) => {
    unbindMutation.mutate(
      { evotorId: store.evotorId },
      {
        onSuccess: () =>
          onSuccess(t('retailSettings.stores.unbindFeedback', { name: store.name || store.evotorId })),
        onError: (error) => onError(error.message),
      },
    );
  };

  /** Turning stock export OFF is immediate (no risk); turning it ON opens the confirm dialog
   * below, since it overwrites the quantity a cashier currently sees at that store's till. */
  const handleStockExportChange = (store: RetailEvotorStore, nextEnabled: boolean) => {
    if (nextEnabled) {
      setStockExportConfirmStore(store);
      return;
    }
    stockExportMutation.mutate(
      { evotorId: store.evotorId, enabled: false },
      {
        onSuccess: () =>
          onSuccess(
            t('retailSettings.stores.stockExportDisabledFeedback', {
              name: store.name || store.evotorId,
            }),
          ),
        onError: (error) => onError(error.message),
      },
    );
  };

  const confirmStockExportOn = () => {
    const store = stockExportConfirmStore;
    if (!store) return;
    setStockExportConfirmStore(null);
    stockExportMutation.mutate(
      { evotorId: store.evotorId, enabled: true },
      {
        onSuccess: () =>
          onSuccess(
            t('retailSettings.stores.stockExportEnabledFeedback', {
              name: store.name || store.evotorId,
            }),
          ),
        onError: (error) => onError(error.message),
      },
    );
  };

  /**
   * `nextCode === ''` picks the placeholder ("not bound") — an unbind. Anything else targets a
   * list: a currently-unbound store binds immediately; a currently-bound one needs the
   * rebind-replace confirm (design spec §8.7) naming which list it moves FROM.
   */
  const handlePriceListChange = (store: RetailEvotorStore, nextCode: string) => {
    if (!store.storeCode) return;
    const current = priceListsOverview.bindingByStore.get(store.storeCode);
    if ((current?.code ?? '') === nextCode) return;
    if (!current) {
      bindPriceListMutation.mutate(
        { code: nextCode, storeCode: store.storeCode },
        {
          onSuccess: () =>
            onSuccess(
              t('retailSettings.stores.priceListBoundFeedback', {
                name: store.name || store.evotorId,
                list: priceListsOverview.lists.find((l) => l.code === nextCode)?.name ?? nextCode,
              }),
            ),
          onError: (error) => onError(error.message),
        },
      );
      return;
    }
    setPriceListAction({
      store,
      currentCode: current.code,
      currentName: current.name,
      nextCode: nextCode === '' ? null : nextCode,
      nextName: nextCode === '' ? undefined : priceListsOverview.lists.find((l) => l.code === nextCode)?.name,
    });
  };

  const confirmPriceListAction = () => {
    const action = priceListAction;
    if (!action || !action.store.storeCode) return;
    const storeCode = action.store.storeCode;
    setPriceListAction(null);
    if (action.nextCode) {
      bindPriceListMutation.mutate(
        { code: action.nextCode, storeCode },
        {
          onSuccess: () =>
            onSuccess(
              t('retailSettings.stores.priceListBoundFeedback', {
                name: action.store.name || action.store.evotorId,
                list: action.nextName ?? action.nextCode,
              }),
            ),
          onError: (error) => onError(error.message),
        },
      );
    } else {
      unbindPriceListMutation.mutate(
        { code: action.currentCode, storeCode },
        {
          onSuccess: () =>
            onSuccess(
              t('retailSettings.stores.priceListUnboundFeedback', {
                name: action.store.name || action.store.evotorId,
              }),
            ),
          onError: (error) => onError(error.message),
        },
      );
    }
  };

  /** Turning price export OFF is immediate (reverting is safe — the store resumes feed pricing);
   * ON opens the confirm dialog, since it overwrites whatever price a cashier currently sees. */
  const handlePriceExportChange = (store: RetailEvotorStore, nextEnabled: boolean) => {
    if (nextEnabled) {
      setPriceExportConfirmStore(store);
      return;
    }
    priceExportMutation.mutate(
      { evotorId: store.evotorId, enabled: false },
      {
        onSuccess: () =>
          onSuccess(
            t('retailSettings.stores.priceExportDisabledFeedback', { name: store.name || store.evotorId }),
          ),
        onError: (error) => onError(error.message),
      },
    );
  };

  const confirmPriceExportOn = () => {
    const store = priceExportConfirmStore;
    if (!store) return;
    setPriceExportConfirmStore(null);
    priceExportMutation.mutate(
      { evotorId: store.evotorId, enabled: true },
      {
        onSuccess: () =>
          onSuccess(
            t('retailSettings.stores.priceExportEnabledFeedback', { name: store.name || store.evotorId }),
          ),
        onError: (error) => onError(error.message),
      },
    );
  };

  // Tenant-scoped (adr-integration-012) and reversible — runs immediately, no confirmation.
  // `useCallback`d (unlike most handlers in this file) because `emptyState` below is itself
  // memoized and references it directly.
  const runImport = useCallback(() => {
    importMutation.mutate(undefined, {
      onSuccess: () => onSuccess(t('retailSettings.stores.importStarted')),
      onError: (error) => onError(error.message),
    });
  }, [importMutation, onSuccess, onError, t]);

  const importButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        importMutation.isPending ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <CloudDownloadRoundedIcon />
        )
      }
      disabled={!canManage || importMutation.isPending}
      onClick={runImport}
    >
      {t('retailSettings.stores.importButton')}
    </Button>
  );

  const action = canManage ? (
    importButton
  ) : (
    <Tooltip title={t('retailSettings.noPermissionTooltip')}>
      <span>{importButton}</span>
    </Tooltip>
  );

  // plan-platform-001 WP2.3 (design-stock-channels-page §12.2): the store-to-location binding is
  // migrating to the "Sales channels -> Stock" page (inventory-owned store scopes; evotor's
  // location_id is a read-only mirror during the transition -- adr-integration-016 d5). The bind
  // controls below keep working as the transition surface; this notice points merchants at the new
  // home. The strings are real translation keys, not defaultValue fallbacks: an untranslated Russian
  // defaultValue is what a German or Japanese merchant would otherwise have been shown.
  const migrationNotice = (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {t('retailSettings.stockSourceMovedNotice')}{' '}
      <MuiLink component={RouterLink} to="/channels/stock?tab=stores">
        {t('retailSettings.stockSourceMovedLink')}
      </MuiLink>
    </Alert>
  );

  const emptyState = useMemo(() => {
    if (hasFilter) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('retailSettings.accounts.noMatches', { filter: filterInput })}
        </Typography>
      );
    }
    if (!hasAnyAccounts) {
      return (
        <EmptyState
          icon={<StorefrontRoundedIcon sx={{ fontSize: 48 }} />}
          title={t('retailSettings.stores.emptyNoAccountsTitle')}
          action={
            <Button variant="outlined" onClick={onGoToAccountsTab}>
              {t('retailSettings.stores.emptyNoAccountsAction')}
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        icon={<StorefrontRoundedIcon sx={{ fontSize: 48 }} />}
        title={t('retailSettings.stores.emptyTitle')}
        description={t('retailSettings.stores.emptyDescription')}
        action={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<CloudDownloadRoundedIcon />}
              onClick={runImport}
              disabled={importMutation.isPending}
            >
              {t('retailSettings.stores.importButton')}
            </Button>
          ) : undefined
        }
      />
    );
  }, [
    hasFilter,
    filterInput,
    hasAnyAccounts,
    canManage,
    onGoToAccountsTab,
    runImport,
    importMutation.isPending,
    t,
  ]);

  return (
    <>
      <SettingsCard
        title={t('retailSettings.stores.cardTitle')}
        description={t('retailSettings.stores.cardDescription')}
        icon={<StorefrontRoundedIcon />}
        action={action}
        divided={false}
      >
        <Stack spacing={0} sx={{ pt: 1 }}>
          {migrationNotice}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pb: 1.5 }}>
            <TextField
              size="small"
              value={filterInput}
              onChange={(event) => {
                setFilterInput(event.target.value);
                setPage(0);
              }}
              placeholder={t('retailSettings.accounts.searchPlaceholder')}
              aria-label={t('retailSettings.accounts.searchPlaceholder')}
              sx={{ flexGrow: 1, maxWidth: 320 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Tooltip title={t('retailSettings.accounts.refreshAria')}>
              <IconButton
                aria-label={t('retailSettings.accounts.refreshAria')}
                onClick={() => void query.refetch()}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ height: 4 }}>
            {showLinearProgress ? (
              <LinearProgress aria-label={t('retailSettings.refreshingAria')} sx={{ height: 4 }} />
            ) : null}
          </Box>

          {query.isLoading ? (
            <LoadingState label={t('scaffold.loading')} fullHeight={false} />
          ) : query.isError ? (
            <ErrorState
              title={t('retailSettings.stores.loadError')}
              message={query.error?.message ?? t('common:error.unknown')}
              onRetry={() => void query.refetch()}
            />
          ) : stores.length === 0 ? (
            emptyState
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('retailSettings.stores.table.store')}</TableCell>
                      <TableCell>{t('retailSettings.stores.table.evotorId')}</TableCell>
                      <TableCell>{t('retailSettings.stores.table.account')}</TableCell>
                      <TableCell>
                        {/* design spec §8.7's "where it's kept"/"which prices it sells at" hint
                            pair — a header tooltip rather than a caption repeated under every row's select
                            (the mockup's single-row illustration would be noisy at real table
                            scale with many stores). */}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{t('retailSettings.stores.table.location')}</span>
                          <Tooltip title={t('retailSettings.stores.locationHint')}>
                            <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{t('retailSettings.stores.table.priceList')}</span>
                          <Tooltip title={t('retailSettings.stores.priceListHint')}>
                            <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>{t('retailSettings.stores.table.sync')}</TableCell>
                      <TableCell>{t('retailSettings.stores.table.stockExport')}</TableCell>
                      <TableCell>{t('retailSettings.stores.table.priceExport')}</TableCell>
                      <TableCell>{t('retailSettings.stores.table.lastExported')}</TableCell>
                      <TableCell>{t('retailSettings.stores.table.updated')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stores.map((store) => {
                      const updatedAt = formatDateTime(store.updatedAt);
                      const lastExportedAt = formatDateTime(store.lastStockExportAt);
                      const rowBusy =
                        toggleMutation.isPending &&
                        toggleMutation.variables?.evotorId === store.evotorId;
                      const toggleDisabled = !canManage || rowBusy;
                      const toggleControl = (
                        <Switch
                          size="small"
                          checked={store.active ?? false}
                          disabled={toggleDisabled}
                          onChange={() => handleToggle(store)}
                          slotProps={{
                            input: {
                              'aria-label': t('retailSettings.stores.syncToggleAria', {
                                name: store.name || store.evotorId,
                              }),
                            },
                          }}
                        />
                      );

                      // Location bind/unbind control (user-story-integration-024).
                      const bindBusy =
                        (bindMutation.isPending &&
                          bindMutation.variables?.evotorId === store.evotorId) ||
                        (unbindMutation.isPending &&
                          unbindMutation.variables?.evotorId === store.evotorId);
                      const locationControl = (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <RetailEvotorLocationSelect
                            value={store.locationId ?? ''}
                            onChange={(locationId) => handleBind(store, locationId)}
                            locations={locations}
                            label={t('retailSettings.stores.locationSelectLabel')}
                            placeholderLabel={t('retailSettings.stores.locationUnboundOption')}
                            inactiveSuffix={t('retailSettings.stores.locationInactiveSuffix')}
                            disabled={!canManage || locationsUnavailable || bindBusy}
                            sx={{ minWidth: 180 }}
                          />
                          {store.locationId ? (
                            <IconButton
                              size="small"
                              aria-label={t('retailSettings.stores.unbindAria', {
                                name: store.name || store.evotorId,
                              })}
                              disabled={!canManage || bindBusy}
                              onClick={() => handleUnbind(store)}
                            >
                              <LinkOffRoundedIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Stack>
                      );
                      const locationCellControl = !canManage ? (
                        <Tooltip title={t('retailSettings.noPermissionTooltip')}>
                          <span>{locationControl}</span>
                        </Tooltip>
                      ) : locationsUnavailable ? (
                        <Tooltip title={t('retailSettings.stores.locationsLoadError')}>
                          <span>{locationControl}</span>
                        </Tooltip>
                      ) : (
                        locationControl
                      );

                      // Shared-stock hint (AC2) — 2+ *listed* stores resolving to the same
                      // location advertise the same physical stock; the merchant must see this.
                      const shareCount = store.locationId
                        ? (locationShareCounts.get(store.locationId) ?? 0)
                        : 0;
                      const sharesStock = shareCount >= 2;
                      const siblingNames = sharesStock
                        ? stores
                            .filter(
                              (candidate) =>
                                candidate.locationId === store.locationId &&
                                candidate.evotorId !== store.evotorId,
                            )
                            .map((candidate) => candidate.name || candidate.evotorId)
                        : [];
                      const sharedStockChip = sharesStock ? (
                        <Tooltip
                          title={t('retailSettings.stores.sharedStockTooltip', {
                            names: siblingNames.join(', '),
                          })}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            icon={<SyncAltRoundedIcon fontSize="small" />}
                            label={t('retailSettings.stores.sharedStockChipLabel', {
                              count: shareCount - 1,
                            })}
                            sx={{ alignSelf: 'flex-start', maxWidth: 220 }}
                          />
                        </Tooltip>
                      ) : null;

                      // Stock-export switch + "why not exporting" explanation (user-story-025).
                      const stockExportBusy =
                        stockExportMutation.isPending &&
                        stockExportMutation.variables?.evotorId === store.evotorId;
                      const stockExportSwitchDisabled =
                        !canManage || stockExportBusy || store.notExportingReason === 'NOT_BOUND';
                      const stockExportSwitch = (
                        <Switch
                          size="small"
                          checked={store.stockExportEnabled}
                          disabled={stockExportSwitchDisabled}
                          onChange={(event) => handleStockExportChange(store, event.target.checked)}
                          slotProps={{
                            input: {
                              'aria-label': t('retailSettings.stores.stockExportToggleAria', {
                                name: store.name || store.evotorId,
                              }),
                            },
                          }}
                        />
                      );
                      const stockExportControl = !canManage ? (
                        <Tooltip title={t('retailSettings.noPermissionTooltip')}>
                          <span>{stockExportSwitch}</span>
                        </Tooltip>
                      ) : store.notExportingReason === 'NOT_BOUND' ? (
                        <Tooltip title={t('retailSettings.stores.stockExportNoLocationTooltip')}>
                          <span>{stockExportSwitch}</span>
                        </Tooltip>
                      ) : (
                        stockExportSwitch
                      );
                      const reasonKey = store.notExportingReason
                        ? NOT_EXPORTING_REASON_KEYS[store.notExportingReason]
                        : undefined;
                      const reasonText = store.notExportingReason
                        ? reasonKey
                          ? t(`retailSettings.stores.notExportingReasons.${reasonKey}`)
                          : store.notExportingReason
                        : null;

                      // "Price list" select (design spec §8.7) — gated `settings.pricing.manage`,
                      // independently of this panel's own `canManage`. Disabled when the BFF
                      // hasn't yet surfaced this store's `storeCode` (the disclosed contract gap
                      // on `RetailEvotorStoreResponse` — see `settingsTypes.ts`).
                      const boundPriceList = store.storeCode
                        ? priceListsOverview.bindingByStore.get(store.storeCode)
                        : undefined;
                      const priceListBusy =
                        (bindPriceListMutation.isPending &&
                          bindPriceListMutation.variables?.storeCode === store.storeCode) ||
                        (unbindPriceListMutation.isPending &&
                          unbindPriceListMutation.variables?.storeCode === store.storeCode);
                      const priceListSelect = (
                        <RetailEvotorPriceListSelect
                          value={boundPriceList?.code ?? ''}
                          onChange={(nextCode) => handlePriceListChange(store, nextCode)}
                          priceLists={priceListsOverview.lists}
                          label={t('retailSettings.stores.priceListSelectLabel')}
                          placeholderLabel={t('retailSettings.stores.priceListUnboundOption')}
                          inactiveSuffix={t('retailSettings.stores.priceListInactiveSuffix')}
                          disabled={!canManagePricing || !store.storeCode || priceListBusy}
                          sx={{ minWidth: 180 }}
                        />
                      );
                      const priceListCellControl = !canManagePricing ? (
                        <Tooltip title={t('retailSettings.noPermissionTooltip')}>
                          <span>{priceListSelect}</span>
                        </Tooltip>
                      ) : !store.storeCode ? (
                        <Tooltip title={t('retailSettings.stores.priceListNoCodeTooltip')}>
                          <span>{priceListSelect}</span>
                        </Tooltip>
                      ) : (
                        priceListSelect
                      );
                      // "Price export" toggle — the stockExportEnabled twin (adr-integration-017),
                      // with its own "why not exporting" reason (adr-integration-017 as amended
                      // 2026-09-06) mirroring the stock switch above: NOT_BOUND disables the
                      // switch (the backend rejects price export for an unbound store), every
                      // other reason surfaces as an explanatory caption underneath.
                      const priceExportBusy =
                        priceExportMutation.isPending &&
                        priceExportMutation.variables?.evotorId === store.evotorId;
                      const priceExportSwitchDisabled =
                        !canManagePricing ||
                        priceExportBusy ||
                        store.notExportingPriceReason === 'NOT_BOUND';
                      const priceExportSwitch = (
                        <Switch
                          size="small"
                          checked={store.priceExportEnabled}
                          disabled={priceExportSwitchDisabled}
                          onChange={(event) => handlePriceExportChange(store, event.target.checked)}
                          slotProps={{
                            input: {
                              'aria-label': t('retailSettings.stores.priceExportToggleAria', {
                                name: store.name || store.evotorId,
                              }),
                            },
                          }}
                        />
                      );
                      const priceExportControl = !canManagePricing ? (
                        <Tooltip title={t('retailSettings.noPermissionTooltip')}>
                          <span>{priceExportSwitch}</span>
                        </Tooltip>
                      ) : store.notExportingPriceReason === 'NOT_BOUND' ? (
                        <Tooltip title={t('retailSettings.stores.priceExportNoLocationTooltip')}>
                          <span>{priceExportSwitch}</span>
                        </Tooltip>
                      ) : (
                        priceExportSwitch
                      );
                      const priceReasonKey = store.notExportingPriceReason
                        ? NOT_EXPORTING_PRICE_REASON_KEYS[store.notExportingPriceReason]
                        : undefined;
                      const priceReasonText = store.notExportingPriceReason
                        ? priceReasonKey
                          ? t(`retailSettings.stores.notExportingPriceReason.${priceReasonKey}`)
                          : store.notExportingPriceReason
                        : null;

                      return (
                        <TableRow key={store.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {store.name || '—'}
                            </Typography>
                            {store.address ? (
                              <Typography variant="caption" color="text.secondary">
                                {store.address}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                            >
                              {store.evotorId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                            >
                              {store.accountEvotorId ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              {locationCellControl}
                              {sharedStockChip}
                            </Stack>
                          </TableCell>
                          <TableCell>{priceListCellControl}</TableCell>
                          <TableCell>
                            {!canManage ? (
                              <Tooltip title={t('retailSettings.noPermissionTooltip')}>
                                <span>{toggleControl}</span>
                              </Tooltip>
                            ) : (
                              toggleControl
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              {stockExportControl}
                              {reasonText ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ maxWidth: 200 }}
                                >
                                  {reasonText}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              {priceExportControl}
                              {priceReasonText ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ maxWidth: 200 }}
                                >
                                  {priceReasonText}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell>{lastExportedAt ?? '—'}</TableCell>
                          <TableCell>{updatedAt ?? '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <CompactTablePagination
                count={data?.page.totalElements ?? 0}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_event, next) => setPage(next)}
                onRowsPerPageChange={(event) => {
                  setSize(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Stack>
      </SettingsCard>

      {/* Turning stock export ON overwrites the quantity a cashier currently sees at this
          store's till — the one confirmation this panel still asks for (tenant-scoped and
          reversible, but consequential enough to warrant a pause). Loading stores, toggling
          sync, and binding/unbinding are all ordinary, reversible, tenant-scoped actions
          (adr-integration-012) and run immediately with no dialog. */}
      <ConfirmSyncDialog
        open={stockExportConfirmStore !== null}
        title={t('retailSettings.stores.stockExportConfirmTitle', {
          name: stockExportConfirmStore?.name || stockExportConfirmStore?.evotorId || '',
        })}
        body={t('retailSettings.stores.stockExportConfirmBody')}
        warning={t('retailSettings.stores.stockExportConfirmWarning')}
        confirmLabel={t('retailSettings.stores.stockExportConfirmAction')}
        onConfirm={confirmStockExportOn}
        onCancel={() => setStockExportConfirmStore(null)}
      />

      {/* Moving/clearing a store's price-list binding repoints its till prices (design spec
          §8.7) — both directions confirm; the FIRST-TIME bind (currently unbound) does not. */}
      <ConfirmSyncDialog
        open={priceListAction !== null}
        title={
          priceListAction?.nextCode
            ? t('retailSettings.stores.priceListRebindConfirmTitle')
            : t('retailSettings.stores.priceListUnbindConfirmTitle')
        }
        body={
          priceListAction?.nextCode
            ? t('retailSettings.stores.priceListRebindConfirmBody', {
                current: priceListAction.currentName,
              })
            : t('retailSettings.stores.priceListUnbindConfirmBody')
        }
        confirmLabel={
          priceListAction?.nextCode
            ? t('retailSettings.stores.priceListRebindConfirmAction')
            : t('retailSettings.stores.priceListUnbindConfirmAction')
        }
        onConfirm={confirmPriceListAction}
        onCancel={() => setPriceListAction(null)}
      />

      {/* Turning price export ON overwrites whatever price a cashier currently sees at this
          store's till (adr-integration-017's cashier-price trust rule — the inverse of stock
          export, where OBZR is normally the source of truth). OFF is immediate, no dialog. */}
      <ConfirmSyncDialog
        open={priceExportConfirmStore !== null}
        title={t('retailSettings.stores.priceExportConfirmTitle', {
          name: priceExportConfirmStore?.name || priceExportConfirmStore?.evotorId || '',
        })}
        body={t('retailSettings.stores.priceExportConfirmBody')}
        warning={t('retailSettings.stores.priceExportConfirmWarning')}
        confirmLabel={t('retailSettings.stores.priceExportConfirmAction')}
        onConfirm={confirmPriceExportOn}
        onCancel={() => setPriceExportConfirmStore(null)}
      />
    </>
  );
}
